import { IOperatorPromo, Maybe, OperatorPromoKind } from 'src/types/graphql'

/**
 * Operator promotions — a Traditional operator's OWN offer (GAP's «Promoción»
 * column, Torobox's «Hasta 35% off»), mirrored from its website so SpotMe is
 * never pricier than the operator's own headline. NOT a SpotMe discount (those
 * are `getDiscount`, SpotMe Now only). The server decides visibility
 * (Traditional · re-verified within 45 days · not expired) and builds the
 * Spanish `headline`; the client only shortens it for photo pills.
 * Canon: docs/claude/partner-price-parity.md → "Operator promotions".
 */

const MAX_PILL_CHARS = 26

const mxn = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`

/** Short pill text for search/map cards, e.g. «Hasta 35% off», «Promo $1,390». */
export const operatorPromoPill = (
  promo: Maybe<IOperatorPromo> | undefined
): string | null => {
  if (!promo) return null
  const upTo = promo.isUpTo ? 'Hasta ' : ''
  switch (promo.kind) {
    case OperatorPromoKind.PromoPrice:
      return promo.promoPrice ? `Promo ${mxn(promo.promoPrice)}` : 'Promoción'
    case OperatorPromoKind.PercentOff:
      return promo.percentOff ? `${upTo}${promo.percentOff}% off` : 'Promoción'
    case OperatorPromoKind.TermTiers: {
      const top = Math.max(...(promo.tiers ?? []).map((t) => t.percentOff))
      return Number.isFinite(top) ? `Hasta ${top}% off` : 'Promoción'
    }
    case OperatorPromoKind.Prepay:
      return promo.percentOff
        ? `${upTo}${promo.percentOff}% off pago anticipado`
        : 'Promo pago anticipado'
    case OperatorPromoKind.FreeMonths:
      return `${promo.freeMonths ?? 1} mes${(promo.freeMonths ?? 1) === 1 ? '' : 'es'} gratis`
    case OperatorPromoKind.Perk: {
      if (promo.headline.length <= MAX_PILL_CHARS) return promo.headline
      // Cut at a word boundary: «Candado y primer mes de…», never «de s…».
      const cut = promo.headline.slice(0, MAX_PILL_CHARS - 1)
      const lastSpace = cut.lastIndexOf(' ')
      const head = lastSpace > 0 ? cut.slice(0, lastSpace) : cut
      return `${head.replace(/[\s,;:.\-–]+$/, '')}…`
    }
    default:
      return 'Promoción'
  }
}
